import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction
} from "react";
import { Auth } from "aws-amplify";

import { IHouse, IHousesShortInfo, TRentPeriod } from "interfaces/intarfaces";
import { useContextHospi } from "context/ContextHospi";
import { HouseAPI } from "API/houseApi";
import { sortHouses } from "utils/helpers";
import { sortArray } from "utils/fetchMapInfo";
import { resizeImages } from "utils/resizeImages";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { getHouseQueryKey } from "../API/queries/houseApiQueries";

export interface IContextHost {
  housesShortData: IHousesShortInfo[];
  addHouseToList: (house: IHousesShortInfo) => void;
  getDataHouse: (id: string) => void;
  getHousesShortData: () => void;
  currentHouse: IHouse & TRentPeriod;
  setCurrentHouse: Dispatch<SetStateAction<IHouse & TRentPeriod>>;
  setCurrentPhotos: Dispatch<SetStateAction<{ url: string; order?: number }[]>>;
  currentPhotos: string[];
  createRoom: (data: IHouse) => Promise<IHouse>;
  updateRoom: (
    data: Partial<IHouse>,
    currentHouseId: string
  ) => Promise<IHouse & TRentPeriod>;

  deleteRoom: (id: string) => void;
  uploadImages: (
    houseID: string,
    files: File[],
    filesForDelete?: string[],
    orderedNewImages?: { uid: string; name: string; order: number }[]
  ) => void;
  reorderImages: (
    houseID: string,
    order: { file_name: string; order: number }[]
  ) => void;
  publishRoom: (id: string) => void;
  republishRoom: (data: Partial<IHouse>) => Promise<IHousesShortInfo>;
  loading: boolean;
}

export const initialHouse: IHouse & TRentPeriod = {
  house_id: undefined,
  kind: undefined,
  title: "",
  country: "NL",
  province: "",
  postal_code: "",
  house_number: "",
  additional_house_number: "",
  street: "",
  city: "",
  bedroom_area: null,
  house_area: null,
  shared_items: [],
  private_items: [],
  description: "",
  available_from: "",
  available_to: "",
  price: null,
  additional_expenses: null,
  deposit: null,
  min_tennant_age: 0,
  max_tennant_age: 99,
  gender: "",
  spoken_languages: [],
  photos: [],
  rent_dates: []
};

const ContextHost = createContext<IContextHost>({
  housesShortData: [],
  addHouseToList: () => console.log("Info about house"),
  getDataHouse: () => console.log("Info about house"),
  getHousesShortData: () => console.log("Info about house"),
  currentHouse: initialHouse,
  setCurrentHouse: () => console.log("Info about house"),
  currentPhotos: [],
  setCurrentPhotos: () => undefined,
  createRoom: () => new Promise(() => {}),
  updateRoom: () => new Promise(() => {}),
  deleteRoom: () => console.log("Delete room"),
  uploadImages: () => console.log(""),
  reorderImages: () => console.log(""),
  publishRoom: () => console.log("Publish or not room"),
  republishRoom: () => new Promise(() => {}),
  loading: false
});

export const useContextHost = (): IContextHost => {
  return useContext<IContextHost>(ContextHost);
};

export const ContextHostProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [housesShortData, setHousesShortData] = useState<IHousesShortInfo[]>(
    []
  );
  const [currentHouse, setCurrentHouse] = useState<IHouse & TRentPeriod>(
    initialHouse
  );
  const [currentPhotos, setCurrentPhotos] = useState<
    { url: string; order?: number }[]
  >([]);
  const { currentUserRole } = useContextHospi();
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!location.pathname.includes("room-edit")) {
      setCurrentPhotos([]);
    }
  }, [location.pathname]);

  const getHousesShortData = async () => {
    if (currentUserRole === "host") {
      setLoading(true);
      try {
        const { attributes } = await Auth.currentAuthenticatedUser();
        const response = await HouseAPI.getHouses(attributes.sub);
        setHousesShortData(response.sort(sortHouses));
      } catch (error) {
        setHousesShortData([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const addHouseToList = (house: IHousesShortInfo) => {
    if (house.house_id === undefined) return;
    const newList = housesShortData.filter(
      (item) => item.house_id !== house.house_id
    );
    setHousesShortData([...newList, house].sort(sortHouses));
  };

  const getDataHouse = async (id: string) => {
    setCurrentPhotos([]);
    setLoading(true);
    Auth.currentAuthenticatedUser().then(() => {
      HouseAPI.getHouse(id)
        .then((response) => {
          setCurrentHouse(response);
          setCurrentPhotos(
            response?.photos?.length
              ? response.photos
                  .sort(sortArray)
                  .map((item: { url: string; order?: number }) => ({
                    url: item.url,
                    order: item.order
                  }))
                  .sort((a: any, b: any) => a.order - b.order)
              : []
          );
          setLoading(false);
        })
        .catch(() => {
          setCurrentHouse(initialHouse);
          setLoading(false);
        });
    });
  };

  const createRoom = async (data: IHouse): Promise<IHouse & TRentPeriod> => {
    setLoading(true);
    try {
      const response = await HouseAPI.createHouse(data);
      setCurrentHouse(response);
      addHouseToList(response);
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateRoom = async (
    data: Partial<IHouse>,
    currentHouseId: string
  ): Promise<IHouse & TRentPeriod> => {
    setLoading(true);
    try {
      const response = await HouseAPI.updateHouse(currentHouseId, data);
      setCurrentHouse(response);
      addHouseToList(response);
      await queryClient.invalidateQueries({
        queryKey: getHouseQueryKey(currentHouseId)
      });
      return response;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const uploadImages = async (
    houseID: string,
    files: File[],
    filesForDelete?: string[],
    orderedNewImages?: { uid: string; name: string; order: number }[]
  ) => {
    setLoading(true);
    if (!!files.length) {
      const listFiles = await resizeImages(files);
      for (let file of listFiles) {
        await HouseAPI.uploadPhoto(
          houseID,
          file,
          Date.now(),
          orderedNewImages
            ? orderedNewImages.find(({ uid, name }) =>
                orderedNewImages.hasOwnProperty("uid")
                  ? // @ts-ignore
                    uid === file.uid
                  : name === file.name
              )!.order
            : listFiles.indexOf(file) + 1
        );
      }
    }
    if (filesForDelete && !!filesForDelete.length) {
      await HouseAPI.deletePhotos(currentHouse.house_id!, filesForDelete);
    }
    await queryClient.invalidateQueries({
      queryKey: getHouseQueryKey(houseID)
    });
    setLoading(false);
  };

  const reorderImages = async (
    houseID: string,
    order: { file_name: string; order: number }[]
  ) => {
    await HouseAPI.reorderPhotos(houseID, order);
    await queryClient.invalidateQueries({
      queryKey: getHouseQueryKey(houseID)
    });
  };

  const deleteRoom = (id: string) => {
    setLoading(true);
    Auth.currentAuthenticatedUser().then(() => {
      HouseAPI.deleteHouse(id)
        .then(() => {
          setHousesShortData((prev) =>
            [...prev.filter((item) => item.house_id !== id)].sort(sortHouses)
          );
          queryClient.invalidateQueries({
            queryKey: getHouseQueryKey(id)
          });
          setLoading(false);
        })
        .catch(() => setLoading(false));
    });
  };

  const publishRoom = async (id: string) => {
    setLoading(true);
    try {
      const response = await HouseAPI.togglePublishing(id);
      setHousesShortData((prev) =>
        [...prev.filter((item) => item.house_id !== id), response].sort(
          sortHouses
        )
      );
      await queryClient.invalidateQueries({
        queryKey: getHouseQueryKey(id)
      });
    } finally {
      setLoading(false);
    }
  };

  const republishRoom = async (
    data: Partial<IHouse>
  ): Promise<IHousesShortInfo> => {
    setLoading(true);
    try {
      const result = await HouseAPI.republishHouse(
        currentHouse.house_id!,
        data
      );
      await getHousesShortData();
      return result;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHousesShortData();
    // eslint-disable-next-line
  }, [currentUserRole]);

  return (
    <ContextHost.Provider
      value={{
        housesShortData,
        addHouseToList,
        getDataHouse,
        getHousesShortData,
        currentHouse,
        setCurrentHouse,
        //@ts-ignore
        currentPhotos,
        setCurrentPhotos,
        createRoom,
        updateRoom,
        deleteRoom,
        uploadImages,
        publishRoom,
        republishRoom,
        loading,
        reorderImages
      }}
    >
      {children}
    </ContextHost.Provider>
  );
};
